/* ce-just-in — prev/next controls + disabled-state sync for the
   Just In horizontal scroller. Custom cultureEDIT file (not part
   of Horizon 4.1.1). Progressive enhancement: the track is a
   native scroll region and works without JS; this only wires the
   arrow buttons and disables them at the ends.

   Wrapped in an IIFE on purpose. sections/ce-just-in.liquid emits this
   <script> from inside its own body, and the homepage renders that
   section twice (templates/index.json, order 1 and 3). A bare top-level
   `class` is a lexical binding on the global scope, so the second parse
   threw "Identifier 'CeJustIn' has already been declared". The
   customElements.get guard below could not prevent that: the throw
   happens at parse time, before any statement in the file runs. The
   element still worked — the first copy had already defined it — but
   the second script was dead and the error surfaced in the console.
   Found by Lighthouse 2026-08-07. Keep the wrapper if the script tag
   stays in the section body. */
(() => {
  class CeJustIn extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[ref="track"]') || this.querySelector('.ce-justin__track');
      const root = this.closest('.ce-justin') || this;
      this.prev = root.querySelector('[data-ce-justin-prev]');
      this.next = root.querySelector('[data-ce-justin-next]');
      if (!this.track) return;

      this.step = () => Math.max(this.track.clientWidth * 0.8, 224);
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -this.step(), behavior: 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: this.step(), behavior: 'smooth' }));

      this.sync = this.sync.bind(this);
      this.track.addEventListener('scroll', this.sync, { passive: true });
      window.addEventListener('resize', this.sync, { passive: true });
      this.sync();

      this.initDrag();
    }

    /* Click-and-drag panning for mice. The track is a native scroll
       region, so touch already pans with momentum and rubber-banding we
       cannot reproduce by hand — this deliberately only runs for
       pointerType 'mouse' and leaves touch and pen to the browser. The
       section's own hint reads "drag to explore" and, before this, only
       the arrows moved it on a desktop. */
    initDrag() {
      const track = this.track;
      let pressed = false;
      let dragging = false;
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let moved = 0;

      // Below this, treat the gesture as a click on the card. Above it,
      // the pointerup is the end of a drag and the click that follows has
      // to be swallowed or the browser navigates to whichever product
      // happened to be under the cursor.
      const DRAG_THRESHOLD = 5;

      // THE DRAG ONLY STARTS ONCE THE MOUSE HAS MOVED (fixed 2026-09-29).
      // This used to call setPointerCapture on pointerdown. Capture
      // retargets the rest of the gesture to the track, so the click that
      // follows a plain press-and-release landed on the track rather than
      // the card's <a>, and NO mouse click on a card navigated anywhere —
      // Just In on both stores and the Art exhibitions row alike. Touch was
      // never affected (this only runs for mice). Now pointerdown just
      // records the start; capture and is-dragging begin once the pointer
      // has travelled past the threshold, so a click is a normal click.
      this.onPointerDown = (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (track.scrollWidth - track.clientWidth <= 1) return;

        pressed = true;
        dragging = false;
        moved = 0;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      };

      this.onPointerMove = (event) => {
        if (!pressed) return;
        const delta = event.clientX - startX;
        moved = Math.max(moved, Math.abs(delta));

        if (!dragging) {
          if (moved <= DRAG_THRESHOLD) return;
          dragging = true;
          // is-dragging turns off scroll-snap and smooth scrolling for the
          // duration. Both fight per-frame scrollLeft writes: mandatory
          // snapping drags the track back to the nearest card mid-gesture,
          // and smooth behaviour animates toward each new value rather
          // than tracking the cursor, so the track lags and rubber-bands.
          // Removing the class on release lets the snap re-run once and
          // settle the track on a card edge.
          track.classList.add('is-dragging');
          track.setPointerCapture(pointerId);
        }

        track.scrollLeft = startScroll - delta;
      };

      this.onPointerUp = (event) => {
        if (!pressed) return;
        pressed = false;
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture?.(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
      };

      // Cards are links wrapping images, and both are natively draggable.
      // Without this the browser starts its own drag-and-drop a few pixels
      // in, which stops sending pointermove and strands the gesture. It
      // has to hold from the press, before our own drag has started.
      this.onDragStart = (event) => {
        if (pressed) event.preventDefault();
      };

      // Capture phase: after a real drag the <a> must never see this click.
      this.onClickCapture = (event) => {
        if (moved > DRAG_THRESHOLD) {
          event.preventDefault();
          event.stopPropagation();
        }
        moved = 0;
      };

      track.addEventListener('pointerdown', this.onPointerDown);
      track.addEventListener('pointermove', this.onPointerMove);
      track.addEventListener('pointerup', this.onPointerUp);
      track.addEventListener('pointercancel', this.onPointerUp);
      track.addEventListener('dragstart', this.onDragStart);
      track.addEventListener('click', this.onClickCapture, true);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.sync);
      window.removeEventListener('resize', this.sync);

      if (!this.track) return;
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.track.removeEventListener('click', this.onClickCapture, true);
    }

    sync() {
      if (!this.track) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth - 1;
      const scrollable = max > 1;
      if (this.prev) this.prev.disabled = !scrollable || scrollLeft <= 1;
      if (this.next) this.next.disabled = !scrollable || scrollLeft >= max;
      // Drives the grab cursor, so it never appears on a track holding
      // fewer cards than fit — matching the disabled arrows.
      this.track.classList.toggle('is-scrollable', scrollable);
    }
  }

  if (!customElements.get('ce-just-in')) {
    customElements.define('ce-just-in', CeJustIn);
  }
})();
